import { Link } from 'react-router-dom';
import Seo from '@/components/base/Seo';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ContactForm from '@/components/feature/ContactForm';
import { SITE } from '@/data/site';

const CHANNELS = [
  {
    icon: 'ri-mail-line',
    title: '内容纠错与建议',
    text: '发现文章信息过时或有误，请告诉我们具体篇目和段落。',
  },
  {
    icon: 'ri-lightbulb-line',
    title: '选题投稿',
    text: '有想聊的话题，或者已经写好的内容想投给我们。',
  },
  {
    icon: 'ri-team-line',
    title: '栏目合作',
    text: '内容共创、栏目联动等合作意向，欢迎简单说明方向。',
  },
];

export default function ContactPage() {
  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: `联系我们 - ${SITE.name}`,
      url: `${SITE.domain}/contact`,
      inLanguage: 'zh-CN',
      description: `通过留言或邮件联系${SITE.name}编辑部。`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: SITE.domain },
        { '@type': 'ListItem', position: 2, name: '联系我们', item: `${SITE.domain}/contact` },
      ],
    },
  ];

  return (
    <SiteLayout>
      <Seo
        title={`联系我们 - 与${SITE.name}编辑部对话`}
        description={`联系${SITE.name}：内容纠错、选题投稿、栏目合作或其他咨询，都可以通过在线留言或邮件与我们取得联系。`}
        keywords="联系吃瓜网,内容纠错,投稿,栏目合作"
        path="/contact"
        schema={schema}
      />

      <div className="max-w-container mx-auto px-4 md:px-8 pt-8">
        <Breadcrumbs items={[{ label: '首页', path: '/' }, { label: '联系我们' }]} />
      </div>

      <section className="max-w-container mx-auto px-4 md:px-8 pt-8 pb-6">
        <div className="max-w-2xl">
          <h1 className="font-heading text-3xl md:text-4xl font-black text-foreground-950 tracking-tight">
            联系我们
          </h1>
          <p className="mt-4 text-base leading-8 text-foreground-600">
            不管是纠错、投稿还是合作，我们都想知道。填写下面的表单，或者直接发邮件给
            <a href={`mailto:${SITE.email}`} className="text-primary-600 hover:underline ml-1">
              {SITE.email}
            </a>
            。
          </p>
        </div>
      </section>

      <section className="max-w-container mx-auto px-4 md:px-8 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-6">
                在线留言
              </h2>
              <ContactForm />
            </div>
          </div>

          <aside className="lg:col-span-5 space-y-5">
            {CHANNELS.map((c) => (
              <div
                key={c.title}
                className="flex gap-4 p-5 rounded-lg border border-background-200 bg-background-100"
              >
                <span className="shrink-0 w-10 h-10 rounded-md bg-background-50 text-primary-600 flex items-center justify-center">
                  <i className={`${c.icon} text-xl`} />
                </span>
                <div>
                  <h3 className="font-heading text-sm font-semibold text-foreground-950">
                    {c.title}
                  </h3>
                  <p className="mt-1.5 text-sm leading-7 text-foreground-600">{c.text}</p>
                </div>
              </div>
            ))}

            <div className="rounded-lg bg-accent-950 p-6">
              <h3 className="font-heading text-base font-semibold text-background-50">
                想先了解我们？
              </h3>
              <p className="mt-2 text-sm leading-7 text-background-200/80">
                看看我们是怎么做内容的，以及坚持了哪些原则。
              </p>
              <Link
                to="/about"
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-accent-300 hover:underline"
              >
                关于我们
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-arrow-right-line" />
                </span>
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </SiteLayout>
  );
}